import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="py-20 text-center">
        <SEOHead title="文章未找到 - 掌中美母" />
        <p className="text-foreground-500 text-sm mb-4">该文章不存在或已被移除</p>
        <Link
          to="/"
          className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 掌中美母`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleTags={article.tags}
      />

      <article className="py-10 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.author}</span>
            </div>

            <h1 className="text-xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-secondary-600 bg-secondary-50 px-2.5 py-1 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-8 md:mb-10 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 掌中美母`}
              className="w-full h-56 md:h-80 object-cover object-top"
            />
          </div>

          <div
            className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{article.category}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-400">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6 md:mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer"
                >
                  <div className="relative h-40 md:h-44 overflow-hidden">
                    <img
                      src={rel.imageUrl}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-heading font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{rel.date} · {rel.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}